import parsePhoneNumber from 'libphonenumber-js';

import * as Yup from 'yup';
import { EmailValidator, PhoneValidator } from 'types/Validators';
import * as _ from 'lodash-es';
import captureException from './captureException';

Yup.setLocale({
  // use constant translation keys for messages without values
  string: {
    email: 'validations.email',
  },
});

export function buildPhoneSchema(phoneValidator: PhoneValidator): Yup.StringSchema {
  const syntaticalSchema = Yup.string()
    .required('validations.phone')
    .test({
      name: 'phone',
      message: 'validations.phone',
      test: (value) => {
        if (_.isEmpty(value)) {
          return true;
        }

        return parsePhoneNumber(value as string, 'US')?.isValid() ?? false;
      },
    });

  return Yup.string().test('is-legit-phone', 'validations.phone', async (value) => {
    if (!value) {
      return true;
    }

    if (value && (await syntaticalSchema.isValid(value))) {
      try {
        return await phoneValidator(value);
      } catch (e) {
        captureException(e);
        return true;
      }
    }

    return false;
  });
}

export function buildEmailSchema(emailValidator: EmailValidator): Yup.StringSchema {
  const syntaticalSchema = Yup.string().required('validations.email').email();

  return Yup.string().test('is-legit-email', 'validations.email', async (value) => {
    if (value && (await syntaticalSchema.isValid(value))) {
      try {
        return (await emailValidator(value))?.valid;
      } catch (e) {
        captureException(e);
        return true;
      }
    }

    return false;
  });
}

interface BuildLeadFormSchemaOptions {
  emailValidator: EmailValidator;
  phoneValidator: PhoneValidator;
}

export function buildLeadFormSchema({
  emailValidator,
  phoneValidator,
}: BuildLeadFormSchemaOptions): Yup.ObjectSchema<any> {
  return Yup.object().shape({
    name: Yup.string()
      .matches(/^\S{2,} .*\S{2,}\s*$/, 'validations.name')
      .required('validations.name'),
    email: buildEmailSchema(emailValidator),
    phone: buildPhoneSchema(phoneValidator),
    zip: Yup.string().test(
      'zip-validation',
      'Invalid zip code',
      function zipValidator(value, context) {
        const zipRequired = context.options.context?.zipRequired;
        const isValidFormat = value ? /^\d{5}$/.test(value) : false;
        const zipMessage = 'validations.zip';

        if (zipRequired) {
          if (!value) {
            return this.createError({ message: zipMessage });
          }
          if (!isValidFormat) {
            return this.createError({ message: zipMessage });
          }
        } else if (value && !isValidFormat) {
          return this.createError({ message: zipMessage });
        }

        return true;
      }
    ),
    address: Yup.string().when('$addressRequired', (addressRequired, schema) => {
      const value = Array.isArray(addressRequired) ? addressRequired[0] : addressRequired;
      return value ? schema.required('validations.address') : schema.notRequired();
    }),
  });
}

export function buildLoginFormSchema({
  emailValidator,
}: {
  emailValidator: EmailValidator;
}): Yup.ObjectSchema<any> {
  return Yup.object().shape({
    email: buildEmailSchema(emailValidator),
    password: Yup.string().required(),
  });
}

export function buildEmailFormSchema({
  emailValidator,
}: {
  emailValidator: EmailValidator;
}): Yup.ObjectSchema<any> {
  return Yup.object().shape({
    email: buildEmailSchema(emailValidator),
  });
}

export const ForgotPasswordFormSchema = Yup.object().shape({
  email: Yup.string().email().required(),
});
